import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import apiClient, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { LockKey, ShieldCheck, CreditCard, Lightning } from "@phosphor-icons/react";

const inputCls =
  "w-full h-11 px-3.5 rounded-md border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 focus:outline-none transition";

const PLAN_LABEL = { trial: "Free Trial", starter: "Starter", professional: "Professional", enterprise: "Enterprise", annual: "Annual" };

function BillingCard() {
  const { user, company } = useAuth();
  const navigate = useNavigate();
  const [portalBusy, setPortalBusy] = useState(false);
  const u = company?.usage;
  const isAdmin = user?.role === "admin";

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const r = await apiClient.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.portal_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No billing account yet — choose a plan first.");
      setPortalBusy(false);
    }
  };

  if (!u) return null;
  const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null);
  const resets = fmt(u.period_end);
  const periodStart = fmt(u.period_start);

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="billing-card">
      <div className="flex items-center gap-2 mb-5">
        <CreditCard size={18} weight="bold" className="text-[#0F172A]" />
        <h2 className="font-display font-bold text-lg text-[#0F172A]">Billing &amp; Plan</h2>
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div><div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-slate-400">Current Plan</div><div className="font-display font-bold text-[#0F172A] mt-1" data-testid="billing-plan">{PLAN_LABEL[u.plan] || u.plan}{!u.is_trial && u.interval ? ` · ${u.interval === "year" ? "Annual" : "Monthly"}` : ""}</div></div>
        <div><div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-slate-400">Status</div><div className="font-display font-bold text-[#0F172A] mt-1 capitalize">{u.cancel_at_period_end ? "Cancels at period end" : u.status}</div></div>
        <div><div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-slate-400">Proposals Used</div><div className="font-display font-bold text-[#0F172A] mt-1">{u.unlimited ? `${u.used} · Unlimited` : `${u.used} / ${u.limit}${u.extra_credits > 0 ? ` (+${u.extra_credits})` : ""}`}</div></div>
        <div><div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-slate-400">Remaining</div><div className="font-display font-bold text-[#0F172A] mt-1">{u.unlimited ? "∞" : u.remaining}</div></div>
        {u.seat_limit != null && <div><div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-slate-400">Team Seats</div><div className="font-display font-bold text-[#0F172A] mt-1" data-testid="billing-seats">{u.seats_used ?? 0} / {u.seat_limit}</div></div>}
        {!u.is_trial && periodStart && resets && <div><div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-slate-400">Current Period</div><div className="font-display font-bold text-[#0F172A] mt-1" data-testid="billing-current-period">{periodStart} – {resets}</div></div>}
        {!u.is_trial && resets && <div><div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-slate-400">{u.cancel_at_period_end ? "Access Ends" : "Next Renewal"}</div><div className="font-display font-bold text-[#0F172A] mt-1" data-testid="billing-next-renewal">{resets}</div></div>}
      </div>
      {isAdmin ? (
        <div className="flex flex-wrap gap-2 mt-6">
          <button data-testid="billing-choose-plan-btn" onClick={() => navigate("/pricing")} className="inline-flex items-center gap-2 h-10 px-4 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm transition-colors"><Lightning size={15} weight="fill" /> {u.is_trial ? "Choose Plan" : "Change / Upgrade Plan"}</button>
          <button data-testid="billing-portal-btn" onClick={openPortal} disabled={portalBusy} className="inline-flex items-center gap-2 h-10 px-4 border border-slate-300 rounded-md font-semibold text-sm text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60">{portalBusy ? "Opening…" : "Manage Billing"}</button>
        </div>
      ) : <p className="text-xs text-slate-400 mt-6">Only an admin can manage billing.</p>}
    </div>
  );
}

export default function AccountPage() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("billing")) {
      toast.success("Subscription active — welcome aboard!");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    setSaving(true);
    try {
      await apiClient.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg space-y-6">
      <div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-[#0F172A]">Account</h1>
        <p className="text-slate-500 mt-2">Signed in as {user?.name} ({user?.email}) · {user?.role}</p>
      </div>

      <BillingCard />

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-5">
          <LockKey size={18} weight="bold" className="text-[#0F172A]" />
          <h2 className="font-display font-bold text-lg text-[#0F172A]">Change Password</h2>
        </div>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Current Password</label>
            <input data-testid="current-password-input" type="password" className={`${inputCls} mt-2`} value={current} onChange={(e) => setCurrent(e.target.value)} required />
          </div>
          <div>
            <label className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">New Password</label>
            <input data-testid="new-password-input" type="password" className={`${inputCls} mt-2`} value={next} onChange={(e) => setNext(e.target.value)} required />
            <p className="text-xs text-slate-400 mt-1.5">At least 8 characters.</p>
          </div>
          <div>
            <label className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Confirm New Password</label>
            <input data-testid="confirm-password-input" type="password" className={`${inputCls} mt-2`} value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
          </div>
          <button data-testid="change-password-btn" type="submit" disabled={saving} className="inline-flex items-center gap-2 h-11 px-5 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm transition-colors disabled:opacity-60">
            <ShieldCheck size={17} weight="bold" /> {saving ? "Updating…" : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
